import { type SuretyAccountId, type SuretyQuoteId } from "@inrev/common";
import { AdminBondRequestSubmissionAdapterProvider } from "@portal/components/features/bond-request-submission/AdminSubmissionAdapterProvider";
import { AgentBondRequestSubmissionAdapterProvider } from "@portal/components/features/bond-request-submission/AgentSubmissionAdapterProvider";
import { OutcomeSubscriber } from "@portal/components/features/bond-request-submission/steps/BondRequestSubmissionSubscribers";
import {
	createContext,
	type FC,
	type ReactNode,
	useCallback,
	useContext,
	useMemo,
	useRef,
	useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { useFlags } from "./GlobalFlagsProvider";

export type PendingOutcomeEntry = {
	quoteId: SuretyQuoteId;
	isAdmin: boolean;
	accountId: SuretyAccountId;
};

type PendingBondRequestOutcomeContext = {
	watchOutcome: (entry: PendingOutcomeEntry) => void;
};

const defaultPendingBondRequestOutcomeContext: PendingBondRequestOutcomeContext = {
	watchOutcome: () => {},
};

const PendingBondRequestOutcomeContext = createContext<PendingBondRequestOutcomeContext>(
	defaultPendingBondRequestOutcomeContext,
);

export const usePendingBondRequestOutcomes = (): PendingBondRequestOutcomeContext =>
	useContext(PendingBondRequestOutcomeContext);

type OutcomeWatcherProps = {
	entry: PendingOutcomeEntry;
	onDone: (quoteId: SuretyQuoteId, outcome: "approved" | "review") => void;
};

const OutcomeWatcher: FC<OutcomeWatcherProps> = ({ entry, onDone }) => {
	const handleOutcome = useCallback(
		(outcome: "approved" | "review") => onDone(entry.quoteId, outcome),
		[entry.quoteId, onDone],
	);
	const subscriber = <OutcomeSubscriber quoteId={entry.quoteId} onOutcome={handleOutcome} />;

	return entry.isAdmin ? (
		<AdminBondRequestSubmissionAdapterProvider accountId={entry.accountId}>
			{subscriber}
		</AdminBondRequestSubmissionAdapterProvider>
	) : (
		<AgentBondRequestSubmissionAdapterProvider accountId={entry.accountId}>
			{subscriber}
		</AgentBondRequestSubmissionAdapterProvider>
	);
};

export const PendingBondRequestOutcomeProvider: FC<{ children: ReactNode }> = ({ children }) => {
	const [entries, setEntries] = useState<PendingOutcomeEntry[]>([]);
	const entriesRef = useRef(entries);
	entriesRef.current = entries;
	const { showSuccessFlag, showInfoFlag } = useFlags();
	const navigate = useNavigate();

	const watchOutcome = useCallback((entry: PendingOutcomeEntry) => {
		setEntries((prev) =>
			prev.some((existing) => existing.quoteId === entry.quoteId) ? prev : [...prev, entry],
		);
	}, []);

	const handleDone = useCallback(
		(quoteId: SuretyQuoteId, outcome: "approved" | "review") => {
			const entry = entriesRef.current.find((watched) => watched.quoteId === quoteId);
			setEntries((prev) => prev.filter((watched) => watched.quoteId !== quoteId));
			if (!entry) return;

			const detailsPath = `/accounts/${entry.accountId}/requests/${quoteId}`;
			const showFlag = outcome === "approved" ? showSuccessFlag : showInfoFlag;
			showFlag({
				heading:
					outcome === "approved"
						? "Bond Request Approved"
						: "An underwriter is reviewing your bond request",
				message:
					outcome === "approved"
						? "Click below to review and accept the terms."
						: "Click below to confirm extracted details and provide any missing information.",
				actions: [{ label: "View Bond Request", onClick: () => navigate(detailsPath) }],
			});
		},
		[navigate, showInfoFlag, showSuccessFlag],
	);

	const value = useMemo(() => ({ watchOutcome }), [watchOutcome]);

	return (
		<PendingBondRequestOutcomeContext.Provider value={value}>
			{entries.map((entry) => (
				<OutcomeWatcher key={entry.quoteId} entry={entry} onDone={handleDone} />
			))}
			{children}
		</PendingBondRequestOutcomeContext.Provider>
	);
};
